// uiarc: select (https://uiarc.dev/r/select.json) — Base UI + Tailwind'e çevrildi.
// Panel tetikleyicinin altında (uiarc gibi `popper`: `alignItemWithTrigger` varsayılan kapalı) kenarından büyür
// (`pop-motion`), seçili öğenin işareti küçülmüş ve bulanık gelir. Gösterilen değer listedeki yöne göre döner:
// sonraki seçenek aşağıdan yükselir, öncekisi yukarıdan iner (`items` verilmişse yön bilinir, yoksa hep yukarı).
// uiarc'ın etiket/açıklama/seçenek dizisi paketi yerine proje bileşimi (`Field` + parçalar) korundu.
import * as React from 'react'
import { Select as SelectPrimitive } from '@base-ui/react/select'
import { AnimatePresence, motion, useIsPresent, useReducedMotion } from 'motion/react'
import type { Variants } from 'motion/react'
import { motionTokens } from '@/lib/motion'
import { itemValues } from '@/lib/select-items'
import { cn } from '@/lib/utils'
import { ChevronDownIcon, CheckIcon, ChevronUpIcon } from 'lucide-react'

const { blur, duration, ease } = motionTokens

type SelectState = { value: unknown; order: readonly unknown[] }
const SelectStateContext = React.createContext<SelectState>({ value: null, order: [] })

/** Base UI `Select.Root`; ek olarak seçili değeri izler, `SelectValue` dönüş yönünü buradan alır. */
function Select<Value, Multiple extends boolean | undefined = false>(
  props: SelectPrimitive.Root.Props<Value, Multiple>,
) {
  const { value: valueProp, defaultValue, onValueChange, items } = props
  const [inner, setInner] = React.useState<unknown>(defaultValue ?? null)
  const value = valueProp !== undefined ? valueProp : inner
  const order = React.useMemo(() => itemValues(items), [items])
  const state = React.useMemo(() => ({ value, order }), [value, order])
  return (
    <SelectStateContext.Provider value={state}>
      <SelectPrimitive.Root
        {...props}
        onValueChange={(next, details) => {
          setInner(next)
          onValueChange?.(next, details)
        }}
      />
    </SelectStateContext.Provider>
  )
}

function SelectGroup({ className, ...props }: SelectPrimitive.Group.Props) {
  return (
    <SelectPrimitive.Group
      data-slot="select-group"
      className={cn('scroll-my-1', className)}
      {...props}
    />
  )
}

/** Gösterilen değer yönüne göre döner; reduced-motion altında kısa solma. */
const valueRoll: Variants = {
  enter: (direction: number) => ({
    opacity: 0,
    y: `${direction * 0.35}em`,
    filter: `blur(${blur.soft}px)`,
  }),
  center: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: duration.standard, ease: ease.enter },
  },
  exit: (direction: number) => ({
    opacity: 0,
    y: `${direction * -0.3}em`,
    filter: `blur(${blur.subtle}px)`,
    transition: { duration: duration.fast, ease: ease.standard },
  }),
}
const valueFade: Variants = {
  enter: { opacity: 0 },
  center: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: duration.instant } },
  exit: { opacity: 0, transition: { duration: duration.instant } },
}

/**
 * Bir değer yüzü. Çıkarken Base UI'nin `Value`'su zaten yeni etiketi gösterir; bu yüzden çıkan yüz, kendi
 * son içeriğinin kopyasını (anlık görüntü) gösterir.
 */
function ValueFace({ children, className }: { children: React.ReactNode; className?: string }) {
  const present = useIsPresent()
  const ref = React.useRef<HTMLSpanElement>(null)
  const [snapshot, setSnapshot] = React.useState('')
  React.useLayoutEffect(() => {
    const node = ref.current
    if (!node || !present) return
    const take = () => setSnapshot(node.innerHTML)
    take()
    const observer = new MutationObserver(take)
    observer.observe(node, { childList: true, subtree: true, characterData: true })
    return () => observer.disconnect()
  }, [present])
  return present ? (
    <span ref={ref} className={className}>
      {children}
    </span>
  ) : (
    <span className={className} dangerouslySetInnerHTML={{ __html: snapshot }} />
  )
}

function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  const reduced = useReducedMotion()
  const { value, order } = React.useContext(SelectStateContext)
  const key = JSON.stringify(value ?? null)
  const index = order.findIndex((entry) => JSON.stringify(entry) === key)
  const [previous, setPrevious] = React.useState({ key, index, direction: 1 })
  if (previous.key !== key) {
    setPrevious({
      key,
      index,
      direction: index >= 0 && previous.index >= 0 && index < previous.index ? -1 : 1,
    })
  }
  const direction = previous.direction
  return (
    <span
      data-slot="select-value-roll"
      className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)] [&>*]:col-start-1 [&>*]:row-start-1"
    >
      <AnimatePresence initial={false} custom={direction}>
        <motion.span
          key={key}
          custom={direction}
          variants={reduced ? valueFade : valueRoll}
          initial="enter"
          animate="center"
          exit="exit"
          className="min-w-0"
        >
          <ValueFace className="block min-w-0">
            <SelectPrimitive.Value
              data-slot="select-value"
              className={cn(
                'block min-w-0 overflow-hidden text-left text-ellipsis whitespace-nowrap',
                className,
              )}
              {...props}
            />
          </ValueFace>
        </motion.span>
      </AnimatePresence>
    </span>
  )
}

function SelectTrigger({
  className,
  size = 'default',
  children,
  ...props
}: SelectPrimitive.Trigger.Props & {
  size?: 'sm' | 'default'
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        // Tetikleyici panelin çapası: basınca ölçeklenmez, geri bildirim yalnız renkte.
        'group/select-trigger border-border bg-surface text-foreground rounded-control flex w-full items-center justify-between gap-3 border px-3 text-left text-sm whitespace-nowrap select-none',
        'ease-standard transition-[border-color,background-color,box-shadow] duration-(--duration-fast) motion-reduce:transition-none',
        'hover:not-data-disabled:border-border-strong hover:not-data-disabled:bg-surface-muted active:not-data-disabled:border-border-strong active:not-data-disabled:bg-surface-muted data-popup-open:border-border-strong data-popup-open:bg-surface-muted',
        'data-placeholder:text-text-muted aria-invalid:border-danger',
        'data-disabled:cursor-not-allowed data-disabled:opacity-50',
        'data-[size=default]:h-(--control-height-md) data-[size=sm]:h-(--control-height-sm)',
        "*:data-[slot=select-value-roll]:min-w-0 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon className="text-text-muted pointer-events-none inline-flex flex-none [transition:rotate_var(--duration-spring)_var(--ease-spring),color_var(--duration-fast)_var(--ease-standard)] group-data-popup-open/select-trigger:rotate-180 motion-reduce:transition-none">
        <ChevronDownIcon className="size-4" strokeWidth={1.75} />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  side = 'bottom',
  sideOffset = 4,
  align = 'center',
  alignOffset = 0,
  collisionPadding = 12,
  alignItemWithTrigger = false,
  ...props
}: SelectPrimitive.Popup.Props &
  Pick<
    SelectPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset' | 'alignItemWithTrigger' | 'collisionPadding'
  >) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        collisionPadding={collisionPadding}
        alignItemWithTrigger={alignItemWithTrigger}
        className="isolate z-85"
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          data-align-trigger={alignItemWithTrigger}
          className={cn(
            'pop-motion border-border bg-surface-raised text-foreground shadow-floating relative isolate z-85 max-h-[min(320px,var(--available-height))] w-(--anchor-width) max-w-[min(24rem,calc(100vw-20px))] min-w-(--anchor-width) overflow-x-hidden overflow-y-auto rounded-[calc(var(--radius-control)+2px)] border p-1.5 outline-none',
            '[--pop-ease:var(--ease-enter)] [--pop-exit:var(--duration-instant)] [--pop-fade:var(--duration-standard)] [--pop-move:var(--duration-standard)] [--pop-offset:6px]',
            className,
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List className="py-0.5">{children}</SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({ className, ...props }: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      className={cn(
        'text-text-muted leading-body px-[11px] pt-2 pb-1 text-xs font-medium',
        className,
      )}
      {...props}
    />
  )
}

function SelectItem({ className, children, ...props }: SelectPrimitive.Item.Props) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        'text-foreground relative flex min-h-9 w-full cursor-default items-center gap-2 rounded-[calc(var(--radius-control)-4px)] py-0 pr-[34px] pl-[11px] text-sm outline-none select-none',
        'ease-standard transition-[background-color,color] duration-(--duration-instant) motion-reduce:transition-none',
        'data-highlighted:bg-surface-muted data-disabled:pointer-events-none data-disabled:opacity-45',
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className="flex min-w-0 flex-1 shrink-0 gap-2 whitespace-nowrap">
        {children}
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator
        render={
          <span className="text-foreground pointer-events-none absolute right-2.5 flex size-4 animate-[indicator-in_var(--duration-standard)_var(--ease-enter)_40ms_both] items-center justify-center motion-reduce:animate-none" />
        }
      >
        <CheckIcon className="pointer-events-none" strokeWidth={1.75} />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({ className, ...props }: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('bg-border-subtle pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  )
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn(
        "bg-surface-raised text-text-muted top-0 z-10 grid h-7 w-full cursor-default place-items-center [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <ChevronUpIcon strokeWidth={1.75} />
    </SelectPrimitive.ScrollUpArrow>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn(
        "bg-surface-raised text-text-muted bottom-0 z-10 grid h-7 w-full cursor-default place-items-center [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <ChevronDownIcon strokeWidth={1.75} />
    </SelectPrimitive.ScrollDownArrow>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
